import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

import { showErrorToUser } from '@pletly/ui';
import { blurFaces as getBlurredFacesOnImages, useFaceDetection } from '../../services';
import { BlurredImages, ImageWithIndex } from '../../types';
import { useReferenceImage } from './useReferenceImage';

interface Props {
	images: ImageWithIndex[];
	defaultReferenceImageUrl?: string;
	blurredImages: BlurredImages;
	setBlurredImages: Dispatch<SetStateAction<BlurredImages>>;
}

export const useBlurFaces = ({
	images,
	defaultReferenceImageUrl,
	blurredImages,
	setBlurredImages,
}: Props) => {
	const { loading: isLoadingModels } = useFaceDetection();
	const { selectReferenceImage, referenceImage, getReferenceImageFaceDescriptor } =
		useReferenceImage(defaultReferenceImageUrl);

	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const {
		value: isProcessing,
		setTrue: setIsProcessing,
		setFalse: setProcessingComplete,
	} = useBoolean(false);

	const currentImage = images[currentImageIndex];
	const currentImageBlur = blurredImages[currentImageIndex];

	const currentImageToDisplay = {
		...currentImage,
		...currentImageBlur,
		isBlurred: !!currentImageBlur,
	};

	const goToNextImage = useCallback(() => {
		setCurrentImageIndex((index) => (index + 1) % images.length);
	}, [images.length]);

	const goToPreviousImage = useCallback(() => {
		setCurrentImageIndex((index) => (index - 1 + images.length) % images.length);
	}, [images.length]);

	const undoCurrentBlur = useCallback(() => {
		setBlurredImages((currentlyBlurred) => {
			const updatedImages = [...currentlyBlurred];
			updatedImages[currentImage.mediaIndex] = undefined;

			return updatedImages;
		});
	}, [currentImage, setBlurredImages]);

	const isNext = currentImageIndex < images.length - 1;
	const isPrevious = currentImageIndex > 0;

	const onAddReferencePhotoPress = useCallback(
		() => selectReferenceImage(),
		[selectReferenceImage],
	);

	const blurFacesOn = useCallback(
		async (images: ImageWithIndex[]) => {
			setIsProcessing();

			const faceDescriptor = await getReferenceImageFaceDescriptor();
			if (!faceDescriptor) {
				return setProcessingComplete();
			}

			try {
				const blurredImagesBase64 = await getBlurredFacesOnImages(images, [faceDescriptor]);

				if (images.length > 1) {
					return setBlurredImages(
						images.map((image, index) => getUpdatedImage(image, blurredImagesBase64[index])),
					);
				}

				const [blurredImage] = blurredImagesBase64;

				setBlurredImages((currentlyBlurred) => {
					const updatedImages = [...currentlyBlurred];
					updatedImages[currentImage.mediaIndex] = getUpdatedImage(currentImage, blurredImage);

					return updatedImages;
				});
			} catch (error) {
				console.error(error);

				showErrorToUser(error as Error);
			} finally {
				setProcessingComplete();
			}
		},
		[
			setIsProcessing,
			setBlurredImages,
			currentImage,
			setProcessingComplete,
			getReferenceImageFaceDescriptor,
		],
	);

	const blurCurrentPhotoFaces = useCallback(() => {
		return blurFacesOn([currentImage]);
	}, [blurFacesOn, currentImage]);

	const blurFacesOnAllPhotos = useCallback(() => {
		return blurFacesOn(images);
	}, [blurFacesOn, images]);

	return {
		loading: isProcessing,
		isLoadingModels,
		currentImage: currentImageToDisplay,
		referenceImage: referenceImage?.file,
		onAddReferencePhotoPress,
		blurCurrentPhotoFaces,
		blurFacesOnAllPhotos,
		goToNextImage,
		goToPreviousImage,
		isNext,
		isPrevious,
		undoCurrentBlur,
	};
};

function getUpdatedImage(image: ImageWithIndex, base64: string) {
	return {
		...image,
		base64,
		file: undefined,
		uri: `data:image/png;base64,${base64}`,
	};
}
